import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const UnreadMessagesContext = createContext();

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get current user
    const getCurrentUser = async () => {
      const { data: { user: u } } = await supabase.auth.getUser();
      setUser(u);
    };
    getCurrentUser();
  }, []);

  const fetchUnreadCount = async () => {
    if (!user) return;

    try {
      // Get current user's vet record
      const { data: currentVet, error: vetError } = await supabase
        .from('vets')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (vetError || !currentVet) {
        console.error('Current user vet not found:', vetError);
        setUnreadCount(0);
        return;
      }
      
      // Count unread messages for this vet
      const { data, error } = await supabase
        .from('messages')
        .select('id')
        .eq('recipient_vet_id', currentVet.id)
        .is('read_at', null);
        
      if (error) {
        console.error('Failed to fetch unread count:', error);
        setUnreadCount(0);
        return;
      }
      
      setUnreadCount(data?.length || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      setUnreadCount(0);
    }
  };

  // Fetch unread count when user changes
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      
      // Set up real-time subscription for messages
      const subscription = supabase
        .channel('messages_changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'messages' 
          }, 
          () => {
            // Refetch count when messages change
            fetchUnreadCount();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      // Get current user's vet record
      const { data: currentVet, error: vetError } = await supabase
        .from('vets')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (vetError || !currentVet) return;
      
      // Mark all unread messages as read
      await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('recipient_vet_id', currentVet.id)
        .is('read_at', null);
        
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const refreshUnreadCount = () => {
    fetchUnreadCount();
  };

  return (
    <UnreadMessagesContext.Provider value={{
      unreadCount,
      markAllAsRead,
      refreshUnreadCount
    }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};



